"use client";

import React, { useState, useRef } from "react";
import Link from "next/link";
import Image from "next/image";

/* ─── Types ─── */
interface OrderItem {
  quantity: number;
  price: number;
  productTitle?: string;
  productImage?: string;
}
interface TrackedOrder {
  _id: string;
  orderId?: string;
  status: string;
  paymentStatus?: string;
  paymentMethod?: string;
  shippingCost?: number;
  orderDate: string;
  trackingId?: string;
  courierName?: string;
  deliveryAgent?: string;
  deliveryConfirmedAt?: string;
  customer: { firstName: string; lastName: string; phone: string; address?: string; city?: string };
  items: OrderItem[];
  totalPrice: number;
}

/* ─── Step Config ─── */
const STEPS = [
  { key: "ordered",          label: "Order Placed",     desc: "We received your order",        color: "#6366f1" },
  { key: "confirmed",        label: "Confirmed",         desc: "Order confirmed & packed",      color: "#0ea5e9" },
  { key: "shipped",          label: "Shipped",           desc: "Handed to courier",             color: "#f59e0b" },
  { key: "out_for_delivery", label: "Out for Delivery",  desc: "Rider on the way to you",       color: "#f97316" },
  { key: "delivered",        label: "Delivered",         desc: "Package delivered!",            color: "#22c55e" },
];
const STATUS_TO_STEP: Record<string, number> = {
  pending: 0, pending_payment: 0,
  confirmed: 1, processing: 1, paid: 1,
  shipped: 2, dispatched: 2, in_transit: 2,
  out_for_delivery: 3,
  delivered: 4, completed: 4,
};
const STATUS_BADGE: Record<string, { label: string; color: string; bg: string; dot: string }> = {
  pending:          { label: "Pending",          color: "#92400e", bg: "#fef3c7", dot: "#f59e0b" },
  pending_payment:  { label: "Pending Payment",  color: "#92400e", bg: "#fef3c7", dot: "#f59e0b" },
  confirmed:        { label: "Confirmed",        color: "#1e40af", bg: "#dbeafe", dot: "#3b82f6" },
  processing:       { label: "Processing",       color: "#1e40af", bg: "#dbeafe", dot: "#3b82f6" },
  paid:             { label: "Paid",             color: "#1e40af", bg: "#dbeafe", dot: "#3b82f6" },
  shipped:          { label: "Shipped",          color: "#4338ca", bg: "#e0e7ff", dot: "#6366f1" },
  out_for_delivery: { label: "Out for Delivery", color: "#075985", bg: "#e0f2fe", dot: "#0ea5e9" },
  delivered:        { label: "Delivered ✓",     color: "#065f46", bg: "#d1fae5", dot: "#22c55e" },
  completed:        { label: "Delivered ✓",     color: "#065f46", bg: "#d1fae5", dot: "#22c55e" },
  cancelled:        { label: "Cancelled",        color: "#991b1b", bg: "#fee2e2", dot: "#ef4444" },
};

/* ─── Step Icons ─── */
const StepIcons = [
  <svg key="0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>,
  <svg key="1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>,
  <svg key="2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 6v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
  <svg key="3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>,
  <svg key="4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>,
];

export default function TrackOrderPage() {
  const [orderId, setOrderId] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState<TrackedOrder | null>(null);
  const [error, setError] = useState("");
  const resultRef = useRef<HTMLDivElement>(null);

  const handleTrack = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orderId.trim() || !phone.trim()) return;
    setLoading(true); setError(""); setOrder(null);
    try {
      const res = await fetch("/api/track-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId: orderId.trim(), phone: phone.trim() }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setOrder(data.order);
        setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
      } else {
        setError(data.error || "Order not found.");
      }
    } catch { setError("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  const step = order ? (STATUS_TO_STEP[order.status] ?? 0) : 0;
  const badge = order ? (STATUS_BADGE[order.status] || { label: order.status, color: "#374151", bg: "#f3f4f6", dot: "#9ca3af" }) : null;
  const isCancelled = order?.status === "cancelled";

  return (
    <>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse-ring { 0%,100% { transform: scale(1); opacity:.6 } 50% { transform: scale(1.35); opacity:0 } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
        .track-fade { animation: fadeUp .4s ease both; }
        .track-fade-1 { animation: fadeUp .4s .05s ease both; }
        .track-fade-2 { animation: fadeUp .4s .1s ease both; }
        .track-fade-3 { animation: fadeUp .4s .15s ease both; }
        .track-fade-4 { animation: fadeUp .4s .2s ease both; }
        .pulse-ring {
          position:absolute; inset:-6px; border-radius:50%;
          border: 2.5px solid var(--ring-color);
          animation: pulse-ring 1.8s ease-in-out infinite;
        }
        .track-input {
          width:100%; box-sizing:border-box;
          padding:14px 14px 14px 46px;
          border:1.5px solid #e5e7eb; border-radius:14px;
          font-size:15px; font-weight:500; color:#111827;
          outline:none; background:#f9fafb;
          transition:all .2s;
        }
        .track-input:focus {
          border-color:#B88E2F;
          box-shadow:0 0 0 4px rgba(184,142,47,.12);
          background:white;
        }
        .track-input::placeholder { color:#9ca3af; }
        .step-connector { flex:1; height:3px; border-radius:2px; margin-top:-24px; transition:background .5s; }
        @media (max-width: 640px) {
          .timeline-row { flex-direction:column !important; align-items:flex-start !important; gap:0 !important; }
          .step-connector { display:none !important; }
          .step-col { flex-direction:row !important; align-items:center !important; gap:16px; width:100% !important; padding-bottom:20px; }
          .step-label { text-align:left !important; margin-top:0 !important; }
          .step-col:last-child { padding-bottom:0; }
        }
      `}</style>

      <div style={{ minHeight:"100vh", background:"#f8f7f4", paddingBottom:"4rem" }}>

        {/* ══════════ HERO — Elegant Minimal Header ══════════ */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-28">
          <div className="mb-10 text-left border-b border-gray-100 pb-5">
            <div className="flex items-center gap-2 text-xs font-bold tracking-wider text-[#B88E2F] uppercase mb-1">
              <Link href="/" className="hover:text-gray-950 transition-colors">Home</Link>
              <span className="text-gray-300">/</span>
              <span className="text-gray-400">Track Order</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
              Track Your Order
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1.5 leading-relaxed">
              Your Trusted Partner for School Supplies &amp; Institutional Services in Pakistan.
            </p>
          </div>
        </div>

        {/* ══════════ SEARCH CARD ══════════ */}
        <div style={{ maxWidth:"560px", margin:"0 auto", padding:"0 1.25rem" }}>
          <div style={{ background:"white", borderRadius:"28px", padding:"2.25rem", boxShadow:"0 24px 64px rgba(0,0,0,.13), 0 4px 16px rgba(0,0,0,.06)", border:"1px solid rgba(255,255,255,.9)" }}>

            <form onSubmit={handleTrack} style={{ display:"flex", flexDirection:"column", gap:"1.25rem" }}>

              {/* Order ID */}
              <div>
                <label style={{ display:"block", fontSize:"11px", fontWeight:800, color:"#6b7280", textTransform:"uppercase", letterSpacing:".08em", marginBottom:"8px" }}>
                  Order ID
                </label>
                <div style={{ position:"relative" }}>
                  <div style={{ position:"absolute", left:"14px", top:"50%", transform:"translateY(-50%)", color:"#B88E2F", display:"flex" }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width:"18px", height:"18px" }}>
                      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                    </svg>
                  </div>
                  <input id="track-order-id" type="text" value={orderId} onChange={e => setOrderId(e.target.value)}
                    placeholder="e.g. ORD-12345" required className="track-input" />
                </div>
                <p style={{ margin:"5px 0 0", fontSize:"11px", color:"#9ca3af" }}>Found in your order confirmation SMS or email</p>
              </div>

              {/* Phone */}
              <div>
                <label style={{ display:"block", fontSize:"11px", fontWeight:800, color:"#6b7280", textTransform:"uppercase", letterSpacing:".08em", marginBottom:"8px" }}>
                  Phone Number
                </label>
                <div style={{ position:"relative" }}>
                  <div style={{ position:"absolute", left:"14px", top:"50%", transform:"translateY(-50%)", color:"#B88E2F", display:"flex" }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width:"18px", height:"18px" }}>
                      <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                    </svg>
                  </div>
                  <input id="track-phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                    placeholder="e.g. 03001234567" required className="track-input" />
                </div>
                <p style={{ margin:"5px 0 0", fontSize:"11px", color:"#9ca3af" }}>Must match the number used at checkout</p>
              </div>

              {/* Button */}
              <button type="submit" disabled={loading || !orderId.trim() || !phone.trim()} style={{
                width:"100%", padding:"15px",
                background: loading || !orderId.trim() || !phone.trim()
                  ? "#e5e7eb"
                  : "linear-gradient(135deg,#B88E2F 0%,#8C6D23 100%)",
                color: loading || !orderId.trim() || !phone.trim() ? "#9ca3af" : "white",
                border:"none", borderRadius:"16px", fontSize:"15px", fontWeight:800,
                cursor: loading ? "not-allowed" : "pointer",
                letterSpacing:".02em",
                boxShadow: loading ? "none" : "0 6px 24px rgba(184,142,47,.35)",
                transition:"all .25s",
                display:"flex", alignItems:"center", justifyContent:"center", gap:"10px",
              }}>
                {loading ? (
                  <>
                    <span style={{ width:"18px", height:"18px", border:"2.5px solid rgba(0,0,0,.15)", borderTopColor:"#9ca3af", borderRadius:"50%", display:"inline-block", animation:"spin .7s linear infinite" }} />
                    Searching...
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" style={{ width:"18px", height:"18px" }}>
                      <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                    </svg>
                    Track My Order
                  </>
                )}
              </button>
            </form>

            {/* Error */}
            {error && (
              <div style={{ marginTop:"1.25rem", padding:"14px 16px", background:"#fff1f2", border:"1.5px solid #fecdd3", borderRadius:"14px", display:"flex", gap:"12px", alignItems:"flex-start" }}>
                <div style={{ width:"32px", height:"32px", borderRadius:"50%", background:"#fee2e2", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" style={{ width:"16px", height:"16px" }}>
                    <path d="M18 6L6 18M6 6l12 12"/>
                  </svg>
                </div>
                <div>
                  <p style={{ margin:"0 0 2px", fontSize:"13px", fontWeight:700, color:"#be123c" }}>Order Not Found</p>
                  <p style={{ margin:0, fontSize:"12px", color:"#e11d48" }}>{error}</p>
                </div>
              </div>
            )}

            {/* Helper */}
            <p style={{ marginTop:"1.25rem", fontSize:"12px", color:"#9ca3af", textAlign:"center" }}>
              Signed-in customers →{" "}
              <Link href="/my-orders" style={{ color:"#B88E2F", fontWeight:700, textDecoration:"none" }}>View All Orders</Link>
            </p>
          </div>
        </div>

        {/* ══════════ RESULT ══════════ */}
        {order && (
          <div ref={resultRef} style={{ maxWidth:"900px", margin:"2.5rem auto 0", padding:"0 1.25rem", display:"flex", flexDirection:"column", gap:"1.25rem" }}>

            {/* ── Order Header ── */}
            <div className="track-fade" style={{ background:"white", borderRadius:"24px", padding:"1.75rem 2rem", boxShadow:"0 4px 24px rgba(0,0,0,.07)", border:"1px solid #f0f0f0", display:"flex", flexWrap:"wrap", gap:"1.25rem", alignItems:"center", justifyContent:"space-between" }}>
              <div style={{ display:"flex", alignItems:"center", gap:"1rem" }}>
                <div style={{ width:"52px", height:"52px", borderRadius:"16px", background:"linear-gradient(135deg,#fef3c7,#fde68a)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#B88E2F" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width:"26px", height:"26px" }}>
                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                  </svg>
                </div>
                <div>
                  <p style={{ margin:"0 0 3px", fontSize:"12px", fontWeight:600, color:"#9ca3af", textTransform:"uppercase", letterSpacing:".06em" }}>Order ID</p>
                  <h2 style={{ margin:"0 0 3px", fontSize:"20px", fontWeight:900, color:"#111827", letterSpacing:"-.01em" }}>
                    {order.orderId || `#${order._id.slice(-8).toUpperCase()}`}
                  </h2>
                  <p style={{ margin:0, fontSize:"12.5px", color:"#6b7280" }}>
                    Placed {new Date(order.orderDate).toLocaleDateString("en-PK", { day:"numeric", month:"long", year:"numeric" })}
                    {" at "}
                    {new Date(order.orderDate).toLocaleTimeString("en-PK", { hour:"2-digit", minute:"2-digit" })}
                  </p>
                </div>
              </div>
              {badge && (
                <div style={{ display:"flex", alignItems:"center", gap:"8px", background:badge.bg, padding:"10px 18px", borderRadius:"999px", border:`1.5px solid ${badge.dot}33` }}>
                  <span style={{ width:"8px", height:"8px", borderRadius:"50%", background:badge.dot, display:"inline-block", flexShrink:0 }} />
                  <span style={{ fontSize:"13px", fontWeight:700, color:badge.color, whiteSpace:"nowrap" }}>{badge.label}</span>
                </div>
              )}
            </div>

            {/* ── Cancelled Banner ── */}
            {isCancelled && (
              <div className="track-fade-1" style={{ background:"linear-gradient(135deg,#fff1f2,#ffe4e6)", borderRadius:"20px", padding:"1.5rem 1.75rem", border:"1.5px solid #fecdd3", display:"flex", gap:"1rem", alignItems:"center" }}>
                <div style={{ width:"48px", height:"48px", borderRadius:"14px", background:"#fee2e2", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" style={{ width:"24px", height:"24px" }}>
                    <path d="M18 6L6 18M6 6l12 12"/>
                  </svg>
                </div>
                <div>
                  <p style={{ margin:"0 0 4px", fontWeight:800, color:"#9f1239", fontSize:"15px" }}>Order Cancelled</p>
                  <p style={{ margin:0, color:"#be123c", fontSize:"13px" }}>This order was cancelled. Please contact support if you have any questions.</p>
                </div>
              </div>
            )}

            {/* ── Progress Timeline ── */}
            {!isCancelled && (
              <div className="track-fade-1" style={{ background:"white", borderRadius:"24px", padding:"2rem 2rem 2.25rem", boxShadow:"0 4px 24px rgba(0,0,0,.07)", border:"1px solid #f0f0f0" }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"2rem" }}>
                  <h3 style={{ margin:0, fontSize:"14px", fontWeight:800, color:"#111827", textTransform:"uppercase", letterSpacing:".06em" }}>Delivery Progress</h3>
                  <span style={{ fontSize:"12px", color:"#6b7280", fontWeight:600 }}>Step {step + 1} of {STEPS.length}</span>
                </div>

                {/* Timeline */}
                <div className="timeline-row" style={{ display:"flex", alignItems:"flex-start", gap:"0" }}>
                  {STEPS.map((s, idx) => {
                    const done = idx < step;
                    const current = idx === step;
                    const pending = idx > step;
                    const stepColor = done ? "#B88E2F" : current ? s.color : "#d1d5db";
                    return (
                      <React.Fragment key={s.key}>
                        <div className="step-col" style={{ display:"flex", flexDirection:"column", alignItems:"center", minWidth:0, flex:1 }}>
                          {/* Circle */}
                          <div style={{ position:"relative", marginBottom:"12px" }}>
                            {current && (
                              <span className="pulse-ring" style={{ "--ring-color": s.color } as any} />
                            )}
                            <div style={{
                              width:"48px", height:"48px", borderRadius:"50%",
                              background: done
                                ? "linear-gradient(135deg,#B88E2F,#7a5c1e)"
                                : current
                                ? `linear-gradient(135deg,${s.color},${s.color}cc)`
                                : "#f3f4f6",
                              display:"flex", alignItems:"center", justifyContent:"center",
                              color: pending ? "#9ca3af" : "white",
                              boxShadow: done
                                ? "0 4px 16px rgba(184,142,47,.35)"
                                : current
                                ? `0 4px 20px ${s.color}55`
                                : "none",
                              position:"relative", zIndex:1, flexShrink:0,
                              transition:"all .3s",
                            }}>
                              <div style={{ width:"20px", height:"20px" }}>
                                {done ? (
                                  <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20 6L9 17l-5-5"/>
                                  </svg>
                                ) : StepIcons[idx]}
                              </div>
                            </div>
                          </div>

                          {/* Label */}
                          <div className="step-label" style={{ textAlign:"center", paddingInline:"4px" }}>
                            <p style={{ margin:"0 0 2px", fontSize:"11px", fontWeight: current || done ? 800 : 500, color: done ? "#B88E2F" : current ? "#111827" : "#9ca3af", whiteSpace:"nowrap" }}>
                              {s.label}
                            </p>
                            {current && (
                              <p style={{ margin:0, fontSize:"10px", color:s.color, fontWeight:600 }}>{s.desc}</p>
                            )}
                          </div>
                        </div>

                        {/* Connector */}
                        {idx < STEPS.length - 1 && (
                          <div className="step-connector" style={{ background: done ? "linear-gradient(90deg,#B88E2F,#d4a940)" : "#e5e7eb", marginTop:"24px" }} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Courier / Tracking Banner ── */}
            {(order.courierName || order.trackingId || order.deliveryAgent) && (
              <div className="track-fade-2" style={{ background:"linear-gradient(135deg,#fffbeb,#fef9ee)", borderRadius:"20px", padding:"1.5rem 1.75rem", border:"1.5px solid #fde68a", display:"flex", gap:"1rem", alignItems:"center", flexWrap:"wrap" }}>
                <div style={{ width:"48px", height:"48px", borderRadius:"14px", background:"linear-gradient(135deg,#B88E2F,#7a5c1e)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width:"24px", height:"24px" }}>
                    <rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 6v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
                  </svg>
                </div>
                <div style={{ flex:1 }}>
                  <p style={{ margin:"0 0 6px", fontSize:"12px", fontWeight:800, color:"#78350f", textTransform:"uppercase", letterSpacing:".06em" }}>Shipment Info</p>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:"1rem" }}>
                    {order.courierName && (
                      <span style={{ display:"inline-flex", alignItems:"center", gap:"6px", fontSize:"13px", fontWeight:600, color:"#92400e", background:"#fef3c7", padding:"4px 12px", borderRadius:"999px", border:"1px solid #fcd34d" }}>
                        🚚 {order.courierName}
                      </span>
                    )}
                    {order.trackingId && (
                      <span style={{ display:"inline-flex", alignItems:"center", gap:"6px", fontSize:"13px", fontWeight:600, color:"#92400e", background:"#fef3c7", padding:"4px 12px", borderRadius:"999px", border:"1px solid #fcd34d" }}>
                        🔍 <span style={{ fontFamily:"monospace", letterSpacing:".05em" }}>{order.trackingId}</span>
                      </span>
                    )}
                    {order.deliveryAgent && (
                      <span style={{ display:"inline-flex", alignItems:"center", gap:"6px", fontSize:"13px", fontWeight:600, color:"#92400e", background:"#fef3c7", padding:"4px 12px", borderRadius:"999px", border:"1px solid #fcd34d" }}>
                        👤 {order.deliveryAgent}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── Bottom Grid ── */}
            <div className="track-fade-3" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))", gap:"1.25rem" }}>

              {/* Items */}
              <div style={{ background:"white", borderRadius:"20px", padding:"1.5rem", boxShadow:"0 4px 20px rgba(0,0,0,.06)", border:"1px solid #f0f0f0" }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"1.25rem" }}>
                  <h3 style={{ margin:0, fontSize:"13px", fontWeight:800, color:"#111827", textTransform:"uppercase", letterSpacing:".06em" }}>Items Ordered</h3>
                  <span style={{ fontSize:"12px", fontWeight:700, color:"#B88E2F", background:"#fef3c7", padding:"3px 10px", borderRadius:"999px" }}>{order.items?.length || 0} items</span>
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
                  {order.items?.map((item, idx) => (
                    <div key={idx} style={{ display:"flex", gap:"12px", alignItems:"center", padding:"10px 12px", background:"#fafafa", borderRadius:"14px", border:"1px solid #f3f4f6" }}>
                      {item.productImage ? (
                        <Image src={item.productImage} alt={item.productTitle || "Product"} width={52} height={52}
                          style={{ borderRadius:"10px", objectFit:"cover", flexShrink:0, border:"1px solid #e5e7eb" }} />
                      ) : (
                        <div style={{ width:"52px", height:"52px", borderRadius:"10px", background:"linear-gradient(135deg,#fef3c7,#fde68a)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="#B88E2F" strokeWidth={1.5} style={{ width:"22px", height:"22px" }}>
                            <path d="M20 7H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z"/><path d="M16 3l-4 4-4-4"/>
                          </svg>
                        </div>
                      )}
                      <div style={{ flex:1, minWidth:0 }}>
                        <p style={{ margin:"0 0 3px", fontSize:"13px", fontWeight:700, color:"#111827", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {item.productTitle || "Product"}
                        </p>
                        <p style={{ margin:0, fontSize:"11px", color:"#6b7280" }}>Qty: {item.quantity} × Rs. {item.price.toLocaleString()}</p>
                      </div>
                      <p style={{ margin:0, fontSize:"14px", fontWeight:800, color:"#111827", flexShrink:0 }}>
                        Rs. {(item.price * item.quantity).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
                {/* Price Summary */}
                <div style={{ marginTop:"1rem", paddingTop:"1rem", borderTop:"1.5px dashed #e5e7eb" }}>
                  {order.shippingCost !== undefined && (
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:"6px" }}>
                      <span style={{ fontSize:"13px", color:"#6b7280" }}>Shipping</span>
                      <span style={{ fontSize:"13px", fontWeight:700, color: order.shippingCost === 0 ? "#059669" : "#374151" }}>
                        {order.shippingCost === 0 ? "FREE 🎉" : `Rs. ${order.shippingCost.toLocaleString()}`}
                      </span>
                    </div>
                  )}
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <span style={{ fontSize:"15px", fontWeight:800, color:"#111827" }}>Total Paid</span>
                    <span style={{ fontSize:"22px", fontWeight:900, color:"#B88E2F" }}>Rs. {order.totalPrice?.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Right column */}
              <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>

                {/* Delivery Info */}
                <div style={{ background:"white", borderRadius:"20px", padding:"1.5rem", boxShadow:"0 4px 20px rgba(0,0,0,.06)", border:"1px solid #f0f0f0" }}>
                  <h3 style={{ margin:"0 0 1rem", fontSize:"13px", fontWeight:800, color:"#111827", textTransform:"uppercase", letterSpacing:".06em" }}>Delivering To</h3>
                  <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
                    <DeliveryRow icon={<PersonIcon/>} value={`${order.customer.firstName} ${order.customer.lastName}`} />
                    <DeliveryRow icon={<PhoneIcon/>} value={order.customer.phone} mono />
                    {order.customer.address && <DeliveryRow icon={<PinIcon/>} value={order.customer.address} />}
                    {order.customer.city && <DeliveryRow icon={<CityIcon/>} value={order.customer.city} />}
                    {order.deliveryConfirmedAt && (
                      <div style={{ marginTop:"4px", padding:"10px 12px", background:"#d1fae5", borderRadius:"12px", border:"1px solid #6ee7b7" }}>
                        <p style={{ margin:0, fontSize:"12px", fontWeight:700, color:"#065f46" }}>
                          ✅ Delivered on {new Date(order.deliveryConfirmedAt).toLocaleString("en-PK")}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Payment */}
                <div style={{ background:"white", borderRadius:"20px", padding:"1.5rem", boxShadow:"0 4px 20px rgba(0,0,0,.06)", border:"1px solid #f0f0f0" }}>
                  <h3 style={{ margin:"0 0 1rem", fontSize:"13px", fontWeight:800, color:"#111827", textTransform:"uppercase", letterSpacing:".06em" }}>Payment</h3>
                  <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
                    {order.paymentMethod && (
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                        <span style={{ fontSize:"13px", color:"#6b7280", fontWeight:500 }}>Method</span>
                        <span style={{ fontSize:"13px", fontWeight:700, color:"#111827", textTransform:"uppercase" }}>{order.paymentMethod}</span>
                      </div>
                    )}
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                      <span style={{ fontSize:"13px", color:"#6b7280", fontWeight:500 }}>Status</span>
                      <span style={{
                        fontSize:"12px", fontWeight:800,
                        color: order.paymentStatus === "completed" ? "#065f46" : "#92400e",
                        background: order.paymentStatus === "completed" ? "#d1fae5" : "#fef3c7",
                        padding:"4px 12px", borderRadius:"999px", textTransform:"capitalize",
                      }}>{order.paymentStatus || "pending"}</span>
                    </div>
                  </div>
                </div>

                {/* Help CTA */}
                <div style={{ background:"linear-gradient(135deg,#111827,#1c1208)", borderRadius:"20px", padding:"1.5rem", display:"flex", gap:"14px", alignItems:"center" }}>
                  <div style={{ width:"44px", height:"44px", borderRadius:"14px", background:"rgba(184,142,47,.25)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#B88E2F" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width:"22px", height:"22px" }}>
                      <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                    </svg>
                  </div>
                  <div>
                    <p style={{ margin:"0 0 4px", fontSize:"14px", fontWeight:700, color:"white" }}>Need Help?</p>
                    <p style={{ margin:"0 0 8px", fontSize:"12px", color:"rgba(255,255,255,.5)" }}>Our support team is ready for you</p>
                    <Link href="/contact" style={{ fontSize:"12px", fontWeight:700, color:"#B88E2F", textDecoration:"none", display:"inline-flex", alignItems:"center", gap:"4px" }}>
                      Contact Support
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" style={{ width:"12px", height:"12px" }}>
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </Link>
                  </div>
                </div>

              </div>
            </div>

            {/* Track another */}
            <div className="track-fade-4" style={{ textAlign:"center", paddingTop:".5rem" }}>
              <button onClick={() => { setOrder(null); setOrderId(""); setPhone(""); window.scrollTo({ top:0, behavior:"smooth" }); }}
                style={{ background:"none", border:"1.5px solid #e5e7eb", borderRadius:"12px", padding:"10px 24px", fontSize:"13px", fontWeight:700, color:"#6b7280", cursor:"pointer", transition:"all .2s" }}
                onMouseEnter={e => { (e.target as HTMLButtonElement).style.borderColor = "#B88E2F"; (e.target as HTMLButtonElement).style.color = "#B88E2F"; }}
                onMouseLeave={e => { (e.target as HTMLButtonElement).style.borderColor = "#e5e7eb"; (e.target as HTMLButtonElement).style.color = "#6b7280"; }}
              >
                ← Track Another Order
              </button>
            </div>

          </div>
        )}

      </div>
    </>
  );
}

/* ─── Small helpers ─── */
function DeliveryRow({ icon, value, mono }: { icon: React.ReactNode; value: string; mono?: boolean }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
      <div style={{ width:"28px", height:"28px", borderRadius:"8px", background:"#f3f4f6", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, color:"#6b7280" }}>
        {icon}
      </div>
      <span style={{ fontSize:"13px", fontWeight:600, color:"#374151", fontFamily: mono ? "monospace" : "inherit", letterSpacing: mono ? ".04em" : "normal" }}>{value}</span>
    </div>
  );
}
function PersonIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width:"14px", height:"14px" }}><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>; }
function PhoneIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width:"14px", height:"14px" }}><path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>; }
function PinIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width:"14px", height:"14px" }}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>; }
function CityIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width:"14px", height:"14px" }}><path d="M3 21h18M3 10h18M3 7l9-4 9 4M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3"/></svg>; }
